/** @jsxImportSource @emotion/react */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { ComboBox } from 'components/selects/ComboBox';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomRadio from 'components/inputs/CustomRadio';
import { DefaultProps } from 'pages/md/invest/MdIvesCmpuRegPage';
import { Link, useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { st } from 'components/inputs/CustomInput';
import ModelSimilityPopup, { SmlyPopupProps } from '../../popup/ModelSimilityPopup';
import { MdIvesMdlSmly, MdModelSpecInfo } from 'models/md/MdIves';
import {
  copyMdIves,
  copyMdIvesMdlInfo,
  deleteMdMdlInfo,
  dropMdIves,
  findMdIvesMdlInfo,
  findMdIvesMdlInfoList,
  findSimilarModelsWithScoreList,
  saveMdIvesMdlInfo,
} from 'apis/md/MdProject';
import LineEqpNbuIspt from './input/LineEqpNbuIspt';
import IvesAtcIspt from './input/IvesAtcIspt';
import DtlIsptMatr from './input/DtlIsptMatr';

const IvesInputPage = forwardRef(({ elmPrjId, isEditable }: DefaultProps, ref: React.Ref<any>) => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const modelInit = {
    ivesMdlId: '',
    elmPrjId: elmPrjId || '',
    mdlNm: '',
    mdlDesc: '',
    cfmYn: '',
    stackingType: '',
    cellType: '',
    finishedCellComposition: '',
    finishedCellDirectionType: '',
    finishedCellLength: '',
    finishedCellWidth: '',
    finishedCellThickness: '',
    finishedCellStackCount: '',
    finishedCellCapacityAh: '',
    finishedCellNominalVoltage: '',
    finishedCellCapacityWh: '',
    finishedCellWeight: '',
    electrodeAnodeFoilThickness: '',
    electrodeCathodeFoilThickness: '',
    electrodeTabWidth: '',
    ndAnodeNotchingType: '',
    ndCathodeNotchingType: '',
    pkgSealingSideType: '',
    pkgPouchThickness: '',
    pkgDailyProductionCapacity: '',
    jigFormationCurrent: '',
    jigFormationChannelCount: '',
    chargeDischargePowerSupplySpec: '',
    chargeDischargeChannelCount: '',
    dgsOrientationType: '',
    dgsCellsPerTray: '',
    eolFoldingType: '',
    eolTapingSideCount: '',
    eolTraysPerBox: '',
    eolCellsPerTray: '',
    atchFileGrId: '',
    rmk: '',
    useYn: '',
    optValCtn1: '',
    optValCtn2: '',
    optValCtn3: '',
    optValCtn4: '',
    optValCtn5: '',
    dataInsUserId: '',
    dataInsUserNm: '',
    dataInsUserIp: '',
    dataInsDtm: '',
    dataUpdUserId: '',
    dataUpdUserNm: '',
    dataUpdUserIp: '',
    dataUpdDtm: '',
  };

  const [count, setCount] = useState(1); // 모델 추가시 id값 매핑용
  const [errors, setErrors] = useState<any>({});
  const [modelList, setModelList] = useState<MdModelSpecInfo[]>([]);
  const [modelSpecInfo, setModelSpecInfo] = useState<MdModelSpecInfo>(modelInit);
  const [duplModelSpecInfo, setDuplModelSpecInfo] = useState<MdModelSpecInfo>(modelInit);
  const [selectedSmlyModel, setSelectedSmlyModel] = useState<MdIvesMdlSmly>();
  const [mdlSpecCd, setMdlSpecCd] = useState<Code[]>([]);
  const [mdStackingTp, setMdStackingTp] = useState<Code[]>([]);
  const [mdCellTp, setMdCellTp] = useState<Code[]>([]);
  const [mdCptnTpCd, setMdCptnTpCd] = useState<Code[]>([]);
  const [mdDirTp, setMdDirTp] = useState<Code[]>([]);
  const [mdNochTp, setMdNochTp] = useState<Code[]>([]);
  const [mdSideTp, setMdSideTp] = useState<Code[]>([]);
  const [mdDgsDirTp, setMdDgsDirTp] = useState<Code[]>([]);
  const [mdFoldTp, setMdFoldTp] = useState<Code[]>([]);
  const [mdTapgSide, setMdTapgSide] = useState<Code[]>([]);
  const [mdRolPressTp, setMdRolPressTp] = useState<Code[]>([]);
  const [isOpenSimlModelPopup, setIsOpenSimlModelPopup] = useState<SmlyPopupProps>(null);
  const [isInit, setIsInit] = useState<any>(false); // 투자항목/상세점검사항 initCodes 실행을 위한 상태

  const initCodes = async () => {
    const MDL_SPEC_CD = await getCommonCodeNames('MDL_SPEC_CD'); //	모델 사양코드
    setMdlSpecCd(MDL_SPEC_CD);
    const MD_STACKING_TP = await getCommonCodeNames('MD_STACKING_TP'); //	Stacking Type
    setMdStackingTp(MD_STACKING_TP);
    const MD_CELL_TP = await getCommonCodeNames('MD_CELL_TP'); //	Cell Type
    setMdCellTp(MD_CELL_TP);
    const MD_CPTN_TP_CD = await getCommonCodeNames('MD_CPTN_TP_CD'); //	셀 조성
    setMdCptnTpCd(MD_CPTN_TP_CD);
    const MD_DIR_TP = await getCommonCodeNames('MD_DIR_TP'); //	셀 양/단방향
    setMdDirTp(MD_DIR_TP);
    const MD_NOCH_TP = await getCommonCodeNames('MD_NOCH_TP'); //	Notching Type
    setMdNochTp(MD_NOCH_TP);
    const MD_SIDE_TP = await getCommonCodeNames('MD_SIDE_TP'); //	PKG 3면/4면
    setMdSideTp(MD_SIDE_TP);
    const MD_DGS_DIR_TP = await getCommonCodeNames('MD_DGS_DIR_TP'); //	DGS 수직/수평
    setMdDgsDirTp(MD_DGS_DIR_TP);
    const MD_FOLD_TP = await getCommonCodeNames('MD_FOLD_TP'); //	Folding Type
    setMdFoldTp(MD_FOLD_TP);
    const MD_TAPG_SIDE = await getCommonCodeNames('MD_TAPG_SIDE'); //	EOL Taping 열 수
    setMdTapgSide(MD_TAPG_SIDE);
    const MD_ROL_PRESS_TP = await getCommonCodeNames('MD_ROL_PRESS_TP'); // 양극 롤프레스
    setMdRolPressTp(MD_ROL_PRESS_TP);
  };

  const isModify = () => {
    return !!modelSpecInfo.ivesMdlId && !modelSpecInfo.ivesMdlId.includes('_NEW_');
  };
  const isRegStat = () => {
    return modelSpecInfo.regStatCd === 'REG_CPLT';
  };

  const getSpecName = (code: string) => {
    if (mdlSpecCd.length === 0) return '';

    return mdlSpecCd.find((o) => o.cmnCd === code)?.cmnCdNm;
  };

  const getModelData = async (_ivesMdlId = '') => {
    if (!elmPrjId) return;

    openLoading(true);
    // 형개발 투자비 모델 목록
    const MODEL_LIST = await findMdIvesMdlInfoList({ elmPrjId });
    setModelList(MODEL_LIST);
    if (MODEL_LIST.length > 0) {
      // 선택된 모델 사양 정보 조회
      const ivesMdlId =
        _ivesMdlId || MODEL_LIST.find((o) => o.cfmYn === 'Y')?.ivesMdlId || MODEL_LIST[0].ivesMdlId;
      const MODEL_INFO = await findMdIvesMdlInfo({ elmPrjId, ivesMdlId });
      setModelSpecInfo(_.cloneDeep(MODEL_INFO));
      setDuplModelSpecInfo(_.cloneDeep(MODEL_INFO));

      // 유사모델 조회
      const SMLY_MODEL_LIST = await findSimilarModelsWithScoreList({ elmPrjId, ivesMdlId });
      if (SMLY_MODEL_LIST?.length) {
        setSelectedSmlyModel({ ...SMLY_MODEL_LIST.find((o) => o.cfmYn === 'Y') });
      } else {
        setSelectedSmlyModel(undefined);
      }
    } else {
      btnAdd();
    }
    setErrors(null);
    openLoading(false);
  };

  const btnList = () => {
    navigate(`/md/ives-cmpu`);
  };

  const btnAdd = () => {
    const newModel: MdModelSpecInfo = {
      elmPrjId,
      ivesMdlId: `_NEW_${count}_`,
      mdlNm: `NEW${count}`,
    };
    setModelList((prev) => [...prev, newModel]);
    setModelSpecInfo(newModel);
    setDuplModelSpecInfo(_.cloneDeep(newModel));
    setCount(count + 1);
    setSelectedSmlyModel(undefined);
  };

  const btnCopy = () => {
    if (!modelSpecInfo.ivesMdlId) {
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: String(t('com.msg.해당 모델을 복사하시겠습니까?', '해당 모델을 복사하시겠습니까?')),
      yesCallback: () => {
        openLoading(true);
        copyMdIvesMdlInfo(elmPrjId, modelSpecInfo.ivesMdlId || '')
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.복사 되었습니다.', '복사 되었습니다.'),
              });
              findMdIvesMdlInfoList({ elmPrjId }).then((res) => {
                setModelList(res);
              });
            } else {
              openMessageBar({
                type: 'error',
                content: res.statusCode,
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.복사 중 오류가 발생했습니다.', '복사 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnReset = () => {
    if (modelSpecInfo.ivesMdlId?.includes('_NEW_')) {
      // 신규 추가 모델인 경우
      setModelSpecInfo({ ...modelInit, ivesMdlId: modelSpecInfo.ivesMdlId });
      setSelectedSmlyModel(undefined);
      setErrors(null);
    } else {
      // 이미 저장된 모델인 경우
      getModelData(modelSpecInfo.ivesMdlId);
    }
  };

  const btnChgModel = (item: MdModelSpecInfo) => {
    if (item.ivesMdlId?.includes('_NEW_')) {
      setModelSpecInfo(item);
      setDuplModelSpecInfo(item);
      setSelectedSmlyModel(undefined);
    } else {
      findMdIvesMdlInfo({ elmPrjId, ivesMdlId: item.ivesMdlId }).then((res) => {
        setModelSpecInfo(res);
        setDuplModelSpecInfo(res);
      });
      getModelData(item.ivesMdlId);
    }
  };

  const btnDeleteModel = (item: MdModelSpecInfo) => {
    //
    if (item.ivesMdlId?.includes('_NEW_')) {
      setModelList((prev) => prev.filter((o) => o.ivesMdlId !== item.ivesMdlId));
      setModelSpecInfo({});
      setSelectedSmlyModel({});
    } else {
      openCommonModal({
        modalType: 'confirm',
        content: String(t('md.msg.해당 모델을 삭제하시겠습니까?', '해당 모델을 삭제하시겠습니까?')),
        yesCallback: () => {
          deleteMdMdlInfo({ elmPrjId, ivesMdlId: item.ivesMdlId }).then((res) => {
            setModelList((prev) => prev.filter((o) => o.ivesMdlId !== item.ivesMdlId));
            setModelSpecInfo({});
            setSelectedSmlyModel({});
          });
        },
      });
    }
  };

  const isValid = () => {
    setErrors((prev) => ({
      ...prev,
      mdlNm: !modelSpecInfo.mdlNm,
      stackingType: !modelSpecInfo.stackingType,
      cellType: !modelSpecInfo.cellType,
      chargeDischargeChannelCount: !modelSpecInfo.chargeDischargeChannelCount,
      chargeDischargePowerSupplySpec: !modelSpecInfo.chargeDischargePowerSupplySpec,
      dgsOrientationType: !modelSpecInfo.dgsOrientationType,
      electrodeAnodeFoilThickness: !modelSpecInfo.electrodeAnodeFoilThickness,
      electrodeCathodeFoilThickness: !modelSpecInfo.electrodeCathodeFoilThickness,
      electrodeRolPress: !modelSpecInfo.electrodeRolPress,
      electrodeTabWidth: !modelSpecInfo.electrodeTabWidth,
      eolCellsPerTray: !modelSpecInfo.eolCellsPerTray,
      eolFoldingType: !modelSpecInfo.eolFoldingType,
      eolTapingSideCount: !modelSpecInfo.eolTapingSideCount,
      eolTraysPerBox: !modelSpecInfo.eolTraysPerBox,
      finishedCellCapacityAh: !modelSpecInfo.finishedCellCapacityAh,
      finishedCellComposition: !modelSpecInfo.finishedCellComposition,
      finishedCellDirectionType: !modelSpecInfo.finishedCellDirectionType,
      finishedCellLength: !modelSpecInfo.finishedCellLength,
      finishedCellNominalVoltage: !modelSpecInfo.finishedCellNominalVoltage,
      finishedCellStackCount: !modelSpecInfo.finishedCellStackCount,
      finishedCellThickness: !modelSpecInfo.finishedCellThickness,
      finishedCellWeight: !modelSpecInfo.finishedCellWeight,
      jigFormationChannelCount: !modelSpecInfo.jigFormationChannelCount,
      jigFormationCurrent: !modelSpecInfo.jigFormationCurrent,
      ndAnodeNotchingType: !modelSpecInfo.ndAnodeNotchingType,
      ndCathodeNotchingType: !modelSpecInfo.ndCathodeNotchingType,
      pkgDailyProductionCapacity: !modelSpecInfo.pkgDailyProductionCapacity,
      pkgPouchThickness: !modelSpecInfo.pkgPouchThickness,
      pkgSealingSideType: !modelSpecInfo.pkgSealingSideType,
    }));
    return (
      !!modelSpecInfo.mdlNm &&
      !!modelSpecInfo.stackingType &&
      !!modelSpecInfo.cellType &&
      !!modelSpecInfo.chargeDischargeChannelCount &&
      !!modelSpecInfo.chargeDischargePowerSupplySpec &&
      !!modelSpecInfo.dgsOrientationType &&
      !!modelSpecInfo.electrodeAnodeFoilThickness &&
      !!modelSpecInfo.electrodeCathodeFoilThickness &&
      !!modelSpecInfo.electrodeRolPress &&
      !!modelSpecInfo.electrodeTabWidth &&
      !!modelSpecInfo.eolCellsPerTray &&
      !!modelSpecInfo.eolFoldingType &&
      !!modelSpecInfo.eolTapingSideCount &&
      !!modelSpecInfo.eolTraysPerBox &&
      !!modelSpecInfo.finishedCellCapacityAh &&
      !!modelSpecInfo.finishedCellComposition &&
      !!modelSpecInfo.finishedCellDirectionType &&
      !!modelSpecInfo.finishedCellLength &&
      !!modelSpecInfo.finishedCellNominalVoltage &&
      !!modelSpecInfo.finishedCellStackCount &&
      !!modelSpecInfo.finishedCellThickness &&
      !!modelSpecInfo.finishedCellWeight &&
      !!modelSpecInfo.jigFormationChannelCount &&
      !!modelSpecInfo.jigFormationCurrent &&
      !!modelSpecInfo.ndAnodeNotchingType &&
      !!modelSpecInfo.ndCathodeNotchingType &&
      !!modelSpecInfo.pkgDailyProductionCapacity &&
      !!modelSpecInfo.pkgPouchThickness &&
      !!modelSpecInfo.pkgSealingSideType
    );
  };

  const btnSave = (gbn: string) => {
    if (gbn == 'REG_CPLT' && !isValid()) return;

    openLoading(true);
    saveMdIvesMdlInfo({
      ...modelSpecInfo,
      ivesMdlId: modelSpecInfo.ivesMdlId?.includes('_NEW_') ? '' : modelSpecInfo.ivesMdlId,
      regStatCd: gbn,
      cfmYn:
        modelSpecInfo.ivesMdlId?.includes('_NEW_') && modelList.length === 1
          ? 'Y'
          : modelSpecInfo.cfmYn,
    })
      .then((res) => {
        if (res.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
          });
          getModelData(res.data);
          setIsInit((prev) => !prev);
        } else {
          openMessageBar({
            type: 'error',
            content: res.data || '',
          });
        }
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnCalcSmlyModel = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(
        'md.msg.유사모델 산출 시 투자항목이 변경 될 수 있습니다.<br/>유사모델을 산출하시겠습까?',
        '유사모델 산출 시 투자항목이 변경 될 수 있습니다.<br/>유사모델을 산출하시겠습까?'
      ),
      yesCallback: async () => {
        // 유사모델 산출 전에 모델 사양정보 저장
        openLoading(true);
        const result = await saveMdIvesMdlInfo(modelSpecInfo);
        if (result.successOrNot === 'Y') {
          setIsOpenSimlModelPopup('WRITE');
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.모델 사양정보 저장중 오류가 발생했습니다.',
              '모델 사양정보 저장중 오류가 발생했습니다.'
            ),
          });
          openLoading(false);
        }
      },
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setModelSpecInfo((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: !value }));
  };

  /**
   * @param {string} value - 사용자가 입력한 값
   * @param {number} decimalPlaces - 0이면 정수, 1 이상이면 해당 자릿수까지 소수점 허용
   * @returns {string} 정규식 필터링이 완료된 문자열
   */
  const onlyNumInput = (e, decimalPlaces = 0) => {
    const value = e.target.value;
    // 1. 파라미터가 0일 경우: 정수만 허용 (소수점 입력 자체를 차단)
    if (decimalPlaces === 0) {
      const match = value.match(/^\d*/);
      return match ? match[0] : '';
    }

    // 2. 파라미터가 1 이상일 경우: 지정된 소수점 자릿수까지 허용
    if (decimalPlaces >= 1) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?`);
      const match = value.match(regex);
      return match ? match[0] : '';
    }

    // 음수 등 잘못된 파라미터가 들어올 경우를 대비한 기본값 처리
    return '';
  };

  useImperativeHandle(ref, () => ({
    isDirty: () => !_.isEqual(modelSpecInfo, duplModelSpecInfo),
  }));

  useEffect(() => {
    initCodes();
  }, []);
  useEffect(() => {
    getModelData();
  }, [elmPrjId]);
  useEffect(() => {
    setModelSpecInfo((prev) => ({
      ...prev,
      finishedCellCapacityWh: String(
        Math.round(
          Number(prev.finishedCellCapacityAh || 0) *
            Number(prev.finishedCellNominalVoltage || 0) *
            100
        ) / 100
      ),
    }));
    setDuplModelSpecInfo((prev) => ({
      ...prev,
      finishedCellCapacityWh: String(
        Math.round(
          Number(prev.finishedCellCapacityAh || 0) *
            Number(prev.finishedCellNominalVoltage || 0) *
            100
        ) / 100
      ),
    }));
  }, [modelSpecInfo.finishedCellCapacityAh, modelSpecInfo.finishedCellNominalVoltage]);

  return (
    <>
      <SubTitleLayout>
        <div>
          {modelList.map((o, i) => {
            return (
              <CustomRadio
                key={i}
                id={`ver-radio${i}`}
                label={o.mdlNm || ''}
                name="version"
                checked={o.ivesMdlId === modelSpecInfo.ivesMdlId}
                deleteDisabled={modelList.length <= 1 || !isEditable}
                onChange={(e) => btnChgModel(o)}
                onDelete={() => btnDeleteModel(o)}
              />
            );
          })}
        </div>
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
            {t('com.button.목록', '목록')}
          </Button>
          {isEditable && (
            <>
              <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnAdd}>
                {t('com.button.추가', '추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                disabled={!isModify()}
                disableRipple
                onClick={btnCopy}
              >
                {t('com.button.복사', '복사')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                disabled={!isRegStat()}
                disableRipple
                onClick={btnReset}
              >
                {t('com.button.초기화', '초기화')}
              </Button>
              {modelSpecInfo.regStatCd !== 'REG_CPLT' && (
                <Button
                  css={IconButton.button}
                  className="save"
                  disabled={!elmPrjId || !modelSpecInfo.ivesMdlId || isLoading}
                  onClick={() => btnSave('SAVE')}
                >
                  {t('com.label.임시저장', '임시저장')}
                </Button>
              )}
              <Button
                css={IconButton.button}
                className="save"
                disabled={!elmPrjId || !modelSpecInfo.ivesMdlId || isLoading}
                onClick={() => btnSave('REG_CPLT')}
              >
                {t('com.label.저장', '저장')}
              </Button>
            </>
          )}
        </GlobalBtnGroup>
      </SubTitleLayout>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.모델 사양정보', '모델 사양정보')}</h3>
        </SubTitleGroup>
        <GlobalBtnGroup>
          <ControlBtnGroup className="mr-5">
            <label htmlFor={`main-model-chk`}>{t('md.label.대표모델 지정', '대표모델 지정')}</label>
            <div className="txt">
              <Checkbox
                id={`main-model-chk`}
                css={st.checkbox}
                className="custom"
                checked={modelSpecInfo.cfmYn === 'Y'}
                onChange={(e, c) => {
                  handleOnChange('cfmYn', c ? 'Y' : 'N');
                }}
                disabled={!isEditable}
              />
            </div>
          </ControlBtnGroup>
          {isEditable && (
            <Button
              css={IconButton.button}
              className="confirm"
              disabled={!isModify() || !isRegStat() || isLoading}
              onClick={btnCalcSmlyModel}
            >
              {t('com.label.유사모델 산출', '유사모델 산출')}
            </Button>
          )}
        </GlobalBtnGroup>
      </SubTitleLayout>
      <TableContainer css={tb.customTable} className="colFix9">
        <Table className="!flex">
          <TableBody className="w-1/2 !table">
            <TableRow>
              <TableCell className="header nrb">
                <span>{String(t('mp.label.유사모델', '유사모델'))}</span>
              </TableCell>
              <TableCell className="header"></TableCell>
              <TableCell className="body">
                <button className="link" onClick={() => setIsOpenSimlModelPopup('READ')}>
                  {selectedSmlyModel?.mdlNm}
                </button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{String(t('mp.label.모델명', '모델명'))}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="mdlNm"
                    value={modelSpecInfo.mdlNm}
                    onChange={(e) => handleOnChange('mdlNm', e.target.value)}
                    isError={errors?.mdlNm}
                    placeholder={String(
                      t('md.msg.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.mdlNm}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{getSpecName('S00001')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdStackingTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.stackingType}
                    onChange={(value) => handleOnChange('stackingType', value)}
                    isError={errors?.stackingType}
                    msgError={String(
                      t('md.msg.Stacking Type을 선택해 주세요.', 'Stacking Type을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {mdStackingTp.find((o) => o.cmnCd === modelSpecInfo.stackingType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{getSpecName('S00005')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdCellTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.cellType}
                    onChange={(value) => handleOnChange('cellType', value)}
                    isError={errors?.cellType}
                    msgError={String(
                      t('md.msg.Cell Type을 선택해 주세요.', 'Cell Type을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>{mdCellTp.find((o) => o.cmnCd === modelSpecInfo.cellType)?.cmnCdNm}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={10}>
                {String(t('mp.label.완성 Cell', '완성 Cell'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00020')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdCptnTpCd}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.finishedCellComposition}
                    onChange={(value) => handleOnChange('finishedCellComposition', value)}
                    isError={errors?.finishedCellComposition}
                    msgError={String(t('md.msg.조성을 선택해 주세요.', '조성을 선택해 주세요.'))}
                  />
                ) : (
                  <span>
                    {
                      mdCptnTpCd.find((o) => o.cmnCd === modelSpecInfo.finishedCellComposition)
                        ?.cmnCdNm
                    }
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00025')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdDirTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.finishedCellDirectionType}
                    onChange={(value) => handleOnChange('finishedCellDirectionType', value)}
                    isError={errors?.finishedCellDirectionType}
                    msgError={String(
                      t('md.msg.양/단방향을 선택해 주세요.', '양/단방향을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {
                      mdDirTp.find((o) => o.cmnCd === modelSpecInfo.finishedCellDirectionType)
                        ?.cmnCdNm
                    }
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00030')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellLength"
                    value={modelSpecInfo.finishedCellLength}
                    onChange={(e) => handleOnChange('finishedCellLength', onlyNumInput(e, 1))}
                    isError={errors?.finishedCellLength}
                    placeholder={String(
                      t('md.msg.전장(mm)을 입력해 주세요.', '전장(mm)을 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.전장(mm)을 입력해 주세요.', '전장(mm)을 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellLength}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00035')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellWidth"
                    value={modelSpecInfo.finishedCellWidth}
                    onChange={(e) => handleOnChange('finishedCellWidth', onlyNumInput(e, 1))}
                    isError={errors?.finishedCellWidth}
                    placeholder={String(
                      t('md.msg.전폭(mm)을 입력해 주세요.', '전폭(mm)을 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.전폭(mm)을 입력해 주세요.', '전폭(mm)을 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellWidth}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00040')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellThickness"
                    value={modelSpecInfo.finishedCellThickness}
                    onChange={(e) => handleOnChange('finishedCellThickness', onlyNumInput(e, 1))}
                    isError={errors?.finishedCellThickness}
                    placeholder={String(
                      t('md.msg.두께(mm)를 입력해 주세요.', '두께(mm)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.두께(mm)를 입력해 주세요.', '두께(mm)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellThickness}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00045')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <>
                    <div className="flex">
                      <div className="w-3/6">
                        <CustomInputWithSearch
                          name="finishedCellStackCount"
                          value={modelSpecInfo.finishedCellStackCount}
                          onChange={(e) =>
                            handleOnChange('finishedCellStackCount', onlyNumInput(e))
                          }
                          isError={errors?.finishedCellStackCount}
                          placeholder={String(
                            t('md.msg.Stack 수를 입력해 주세요.', 'Stack 수를 입력해 주세요.')
                          )}
                          msgError={String(
                            t('md.msg.Stack 수를 입력해 주세요.', 'Stack 수를 입력해 주세요.')
                          )}
                        />
                      </div>
                      <div className="w-3/6 text-center">
                        {modelSpecInfo.finishedCellStackCount}
                        {modelSpecInfo.stackingType === 'LNS' ? ' + 1' : ''}
                      </div>
                    </div>
                  </>
                ) : (
                  <span>
                    {modelSpecInfo.finishedCellStackCount}
                    {modelSpecInfo.stackingType === 'LNS' ? ' + 1' : ''}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00050')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellCapacityAh"
                    value={modelSpecInfo.finishedCellCapacityAh}
                    onChange={(e) => handleOnChange('finishedCellCapacityAh', onlyNumInput(e, 1))}
                    isError={errors?.finishedCellCapacityAh}
                    placeholder={String(
                      t('md.msg.Capacity(Ah)을 입력해 주세요.', 'Capacity(Ah)을 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.Capacity(Ah)을 입력해 주세요.', 'Capacity(Ah)을 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellCapacityAh}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00055')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellNominalVoltage"
                    value={modelSpecInfo.finishedCellNominalVoltage}
                    onChange={(e) =>
                      handleOnChange('finishedCellNominalVoltage', onlyNumInput(e, 1))
                    }
                    isError={errors?.finishedCellNominalVoltage}
                    placeholder={String(
                      t(
                        'md.msg.Nominal Voltage(V)을 입력해 주세요.',
                        'Nominal Voltage(V)을 입력해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.Nominal Voltage(V)을 입력해 주세요.',
                        'Nominal Voltage(V)을 입력해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellNominalVoltage}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00060')}</span>
              </TableCell>
              <TableCell className="body">
                <span>{modelSpecInfo.finishedCellCapacityWh}</span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00065')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="finishedCellWeight"
                    value={modelSpecInfo.finishedCellWeight}
                    onChange={(e) => handleOnChange('finishedCellWeight', onlyNumInput(e, 1))}
                    isError={errors?.finishedCellWeight}
                    placeholder={String(
                      t('md.msg.무게(g)를 입력해 주세요.', '무게(g)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.무게(g)를 입력해 주세요.', '무게(g)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.finishedCellWeight}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={4}>
                {String(t('mp.label.전극', '전극'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00100')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="electrodeAnodeFoilThickness"
                    value={modelSpecInfo.electrodeAnodeFoilThickness}
                    onChange={(e) =>
                      handleOnChange('electrodeAnodeFoilThickness', onlyNumInput(e, 1))
                    }
                    isError={errors?.electrodeAnodeFoilThickness}
                    placeholder={String(
                      t(
                        'md.msg.양극 Foil 두께(㎛)를 입력해 주세요.',
                        '양극 Foil 두께(㎛)를 입력해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.양극 Foil 두께(㎛)를 입력해 주세요.',
                        '양극 Foil 두께(㎛)를 입력해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.electrodeAnodeFoilThickness}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00105')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="electrodeCathodeFoilThickness"
                    value={modelSpecInfo.electrodeCathodeFoilThickness}
                    onChange={(e) =>
                      handleOnChange('electrodeCathodeFoilThickness', onlyNumInput(e, 1))
                    }
                    isError={errors?.electrodeCathodeFoilThickness}
                    placeholder={String(
                      t(
                        'md.msg.음극 Foil 두께(㎛)를 입력해 주세요.',
                        '음극 Foil 두께(㎛)를 입력해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.음극 Foil 두께(㎛)를 입력해 주세요.',
                        '음극 Foil 두께(㎛)를 입력해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.electrodeCathodeFoilThickness}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00110')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="electrodeTabWidth"
                    value={modelSpecInfo.electrodeTabWidth}
                    onChange={(e) => handleOnChange('electrodeTabWidth', onlyNumInput(e, 1))}
                    isError={errors?.electrodeTabWidth}
                    placeholder={String(
                      t('md.msg.Tab 폭(mm)를 입력해 주세요.', 'Tab 폭(mm)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.Tab 폭(mm)를 입력해 주세요.', 'Tab 폭(mm)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.electrodeTabWidth}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00115')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdRolPressTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.electrodeRolPress}
                    onChange={(value) => handleOnChange('electrodeRolPress', value)}
                    isError={errors?.electrodeRolPress}
                    msgError={String(
                      t(
                        'md.msg.양극 롤프레스(열간/냉간 싱글/텐덤)를 선택해 주세요.',
                        '양극 롤프레스(열간/냉간 싱글/텐덤)를 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>
                    {mdRolPressTp.find((o) => o.cmnCd === modelSpecInfo.electrodeRolPress)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
          <TableBody className="w-1/2 !table">
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.N&D', 'N&D'))}
              </TableCell>
              <TableCell className="header w200">
                <span className="dot">{getSpecName('S00150')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdNochTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.ndAnodeNotchingType}
                    onChange={(value) => handleOnChange('ndAnodeNotchingType', value)}
                    isError={errors?.ndAnodeNotchingType}
                    msgError={String(
                      t(
                        'md.msg.양극 Notching Type을 선택해 주세요.',
                        '양극 Notching Type을 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>
                    {mdNochTp.find((o) => o.cmnCd === modelSpecInfo.ndAnodeNotchingType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00155')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdNochTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.ndCathodeNotchingType}
                    onChange={(value) => handleOnChange('ndCathodeNotchingType', value)}
                    isError={errors?.ndCathodeNotchingType}
                    msgError={String(
                      t(
                        'md.msg.음극 Notching Type을 선택해 주세요.',
                        '음극 Notching Type을 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>
                    {mdNochTp.find((o) => o.cmnCd === modelSpecInfo.ndCathodeNotchingType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={3}>
                {String(t('mp.label.PKG', 'PKG'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00160')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdSideTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.pkgSealingSideType}
                    onChange={(value) => handleOnChange('pkgSealingSideType', value)}
                    isError={errors?.pkgSealingSideType}
                    msgError={String(
                      t('md.msg.3면/4면을 선택해 주세요.', '3면/4면을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {mdSideTp.find((o) => o.cmnCd === modelSpecInfo.pkgSealingSideType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00165')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="pkgPouchThickness"
                    value={modelSpecInfo.pkgPouchThickness}
                    onChange={(e) => handleOnChange('pkgPouchThickness', onlyNumInput(e, 1))}
                    isError={errors?.pkgPouchThickness}
                    placeholder={String(
                      t('md.msg.Pouch 두께(㎛)을 입력해 주세요.', 'Pouch 두께(㎛)을 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.Pouch 두께(㎛)을 입력해 주세요.', 'Pouch 두께(㎛)을 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.pkgPouchThickness}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00170')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="pkgDailyProductionCapacity"
                    value={modelSpecInfo.pkgDailyProductionCapacity}
                    onChange={(e) =>
                      handleOnChange('pkgDailyProductionCapacity', onlyNumInput(e, 1))
                    }
                    isError={errors?.pkgDailyProductionCapacity}
                    placeholder={String(
                      t(
                        'md.msg.일 생산 Capa(kcell/일)을 선택해 주세요.',
                        '일 생산 Capa(kcell/일)을 선택해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.일 생산 Capa(kcell/일)을 선택해 주세요.',
                        '일 생산 Capa(kcell/일)을 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.pkgDailyProductionCapacity}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.Jig Formation', 'Jig Formation'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00200')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="jigFormationCurrent"
                    value={modelSpecInfo.jigFormationCurrent}
                    onChange={(e) => handleOnChange('jigFormationCurrent', onlyNumInput(e, 1))}
                    isError={errors?.jigFormationCurrent}
                    placeholder={String(
                      t('md.msg.전류(A)를 입력해 주세요.', '전류(A)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.전류(A)를 입력해 주세요.', '전류(A)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.jigFormationCurrent}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00205')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="jigFormationChannelCount"
                    value={modelSpecInfo.jigFormationChannelCount}
                    onChange={(e) => handleOnChange('jigFormationChannelCount', onlyNumInput(e))}
                    isError={errors?.jigFormationChannelCount}
                    placeholder={String(
                      t('md.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.jigFormationChannelCount}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.충방전기', '충방전기'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00210')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="chargeDischargePowerSupplySpec"
                    value={modelSpecInfo.chargeDischargePowerSupplySpec}
                    onChange={(e) =>
                      handleOnChange('chargeDischargePowerSupplySpec', onlyNumInput(e, 1))
                    }
                    isError={errors?.chargeDischargePowerSupplySpec}
                    placeholder={String(
                      t('md.msg.전원사양(A)를 입력해 주세요.', '전원사양(A)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.전원사양(A)를 입력해 주세요.', '전원사양(A)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.chargeDischargePowerSupplySpec}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00215')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="chargeDischargeChannelCount"
                    value={modelSpecInfo.chargeDischargeChannelCount}
                    onChange={(e) => handleOnChange('chargeDischargeChannelCount', onlyNumInput(e))}
                    isError={errors?.chargeDischargeChannelCount}
                    placeholder={String(
                      t('md.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                    )}
                    msgError={String(
                      t('md.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.chargeDischargeChannelCount}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.DGS', 'DGS'))}</TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00220')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdDgsDirTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.dgsOrientationType}
                    onChange={(value) => handleOnChange('dgsOrientationType', value)}
                    isError={errors?.dgsOrientationType}
                    msgError={String(
                      t('md.msg.수직/수평을 선택해 주세요.', '수직/수평을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {mdDgsDirTp.find((o) => o.cmnCd === modelSpecInfo.dgsOrientationType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.EOL', 'EOL'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00230')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdFoldTp}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.eolFoldingType}
                    onChange={(value) => handleOnChange('eolFoldingType', value)}
                    isError={errors?.eolFoldingType}
                    msgError={String(
                      t('md.msg.Folding Type을 선택해 주세요.', 'Folding Type을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {mdFoldTp.find((o) => o.cmnCd === modelSpecInfo.eolFoldingType)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00235')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <ComboBox
                    className="w-3/6"
                    options={mdTapgSide}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={modelSpecInfo.eolTapingSideCount}
                    onChange={(value) => handleOnChange('eolTapingSideCount', value)}
                    isError={errors?.eolTapingSideCount}
                    msgError={String(
                      t('md.msg.Tapping 열 수을 선택해 주세요.', 'Tapping 열 수을 선택해 주세요.')
                    )}
                  />
                ) : (
                  <span>
                    {mdTapgSide.find((o) => o.cmnCd == modelSpecInfo.eolTapingSideCount)?.cmnCdNm}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.포장재 정보', '포장재 정보'))}
              </TableCell>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00240')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="eolTraysPerBox"
                    value={modelSpecInfo.eolTraysPerBox}
                    onChange={(e) => handleOnChange('eolTraysPerBox', onlyNumInput(e))}
                    isError={errors?.eolTraysPerBox}
                    placeholder={String(
                      t(
                        'md.msg.Box당 Tray 수(EA)을 선택해 주세요.',
                        'Box당 Tray 수(EA)을 선택해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.Box당 Tray 수(EA)을 선택해 주세요.',
                        'Box당 Tray 수(EA)을 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.eolTraysPerBox}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                <span className="dot">{getSpecName('S00245')}</span>
              </TableCell>
              <TableCell className="body">
                {isEditable ? (
                  <CustomInputWithSearch
                    name="eolCellsPerTray"
                    value={modelSpecInfo.eolCellsPerTray}
                    onChange={(e) => handleOnChange('eolCellsPerTray', onlyNumInput(e))}
                    isError={errors?.eolCellsPerTray}
                    placeholder={String(
                      t(
                        'md.msg.Tray당 Cell 수(EA)을 선택해 주세요.',
                        'Tray당 Cell 수(EA)을 선택해 주세요.'
                      )
                    )}
                    msgError={String(
                      t(
                        'md.msg.Tray당 Cell 수(EA)을 선택해 주세요.',
                        'Tray당 Cell 수(EA)을 선택해 주세요.'
                      )
                    )}
                  />
                ) : (
                  <span>{modelSpecInfo.eolCellsPerTray}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} rowSpan={3}></TableCell>
            </TableRow>
            <TableRow></TableRow>
            <TableRow></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 라인/설비대수 점검 */}
      <div className="py-3">
        <LineEqpNbuIspt
          elmPrjId={elmPrjId}
          ivesMdlId={modelSpecInfo.ivesMdlId}
          selectedSmlyModel={selectedSmlyModel}
          isInit={isInit}
          setIsInit={setIsInit}
          isEditable={isEditable}
          isRegStat={isRegStat()}
        />
      </div>

      {/* 투자항목 점검 */}
      <div className="py-3">
        <IvesAtcIspt
          elmPrjId={elmPrjId}
          ivesMdlId={modelSpecInfo.ivesMdlId}
          selectedSmlyModel={selectedSmlyModel}
          isInit={isInit}
          setIsInit={setIsInit}
          isEditable={isEditable}
          isRegStat={isRegStat()}
          eolTraysPerBox={modelSpecInfo.eolTraysPerBox}
        />
      </div>

      {/* 상세 점검사항 */}
      <div className="py-3">
        <DtlIsptMatr
          elmPrjId={elmPrjId}
          ivesMdlId={modelSpecInfo.ivesMdlId}
          selectedSmlyModel={selectedSmlyModel}
          isInit={isInit}
          setIsInit={setIsInit}
          isEditable={isEditable}
          isRegStat={isRegStat()}
        />
      </div>
      {!!isOpenSimlModelPopup && (
        <ModelSimilityPopup
          open={!!isOpenSimlModelPopup}
          close={() => setIsOpenSimlModelPopup(null)}
          condition={{
            gbn: isOpenSimlModelPopup,
            elmPrjId: elmPrjId,
            ivesMdlId: modelSpecInfo?.ivesMdlId || '',
          }}
          callbackFn={(model) => {
            setSelectedSmlyModel(model);
            getModelData(modelSpecInfo?.ivesMdlId);
            setIsInit((prev) => !prev);
          }}
        />
      )}
    </>
  );
});

IvesInputPage.displayName = 'IvesInputPage';

export default IvesInputPage;
